import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactElement,
  ReactNode
} from 'react';
import {
  Transforms,
  Editor,
  Path,
  Element as SlateElement,
  NodeEntry
} from 'slate';
import {ReactEditor, useSlateStatic} from 'slate-react';

import {ColorPickerModal} from '../../../components/Toolbar/ToolbarComponents';

// ==================== INTERFACES ====================

interface SelectedCell {
  rowIndex: number;
  colIndex: number;
}

interface ResizeState {
  type: 'col' | 'row' | 'table';
  index?: number;
  startX: number;
  startY?: number;
  startVal: number;
  startHeight?: number;
}

interface TableElement extends SlateElement {
  type: 'table';
  colSizes?: number[];
  width?: number | string;
  height?: number | string;
  marginLeft?: number;
  align?: 'left' | 'center' | 'right';
  headerRow?: boolean;
  striped?: boolean;
  backgroundColor?: string;
  children: SlateElement[];
}

interface TableRowElement extends SlateElement {
  type: 'table-row';
  height?: number;
  children: SlateElement[];
}

interface TableCellElement extends SlateElement {
  type: 'table-cell';
  rowSpan?: number;
  colSpan?: number;
  merged?: boolean;
  backgroundColor?: string;
  children: SlateElement[];
}

// ==================== TABLE UTILITIES ====================

export const TableUtils = {
  findTable: (editor: Editor): NodeEntry<TableElement> | undefined => {
    const nodes = Array.from(
      Editor.nodes(editor, {
        match: (n): n is TableElement =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table',
        mode: 'lowest'
      })
    );

    return nodes[0] as NodeEntry<TableElement> | undefined;
  },

  insertRow: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    rowIndex: number,
    position: 'above' | 'below' = 'below'
  ): void => {
    const [table, tablePath] = tableNode;
    const firstRow = table.children[0] as TableRowElement;
    const colCount = firstRow.children.length;

    const newRow: TableRowElement = {
      type: 'table-row',
      children: Array.from({length: colCount}, () => ({
        type: 'table-cell',
        children: [{text: ''}]
      }))
    };

    const targetIndex = position === 'below' ? rowIndex + 1 : rowIndex;
    const insertPath = [...tablePath, targetIndex];

    Transforms.insertNodes(editor, newRow, {at: insertPath});
  },

  deleteRow: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    rowIndex: number
  ): void => {
    const [table, tablePath] = tableNode;

    if (table.children.length <= 1) {
      Transforms.removeNodes(editor, {at: tablePath});

      return;
    }

    const rowPath = [...tablePath, rowIndex];
    Transforms.removeNodes(editor, {at: rowPath});
  },

  insertColumn: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    colIndex: number,
    position: 'left' | 'right' = 'right'
  ): void => {
    const [table, tablePath] = tableNode;
    const targetColIndex = position === 'right' ? colIndex + 1 : colIndex;

    if (table.colSizes) {
      const newColSizes = [...table.colSizes];
      newColSizes.splice(targetColIndex, 0, 150);
      Transforms.setNodes(editor, {colSizes: newColSizes}, {at: tablePath});
    }

    table.children.forEach((_, rIndex: number) => {
      const cellPath = [...tablePath, rIndex, targetColIndex];
      Transforms.insertNodes(
        editor,
        {type: 'table-cell', children: [{text: ''}]},
        {at: cellPath}
      );
    });
  },

  deleteColumn: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    colIndex: number
  ): void => {
    const [table, tablePath] = tableNode;

    if (table.children[0].children.length <= 1) {
      Transforms.removeNodes(editor, {at: tablePath});

      return;
    }

    if (table.colSizes) {
      const newColSizes = [...table.colSizes];
      newColSizes.splice(colIndex, 1);
      Transforms.setNodes(editor, {colSizes: newColSizes}, {at: tablePath});
    }

    table.children.forEach((_, rIndex: number) => {
      const cellPath = [...tablePath, rIndex, colIndex];
      Transforms.removeNodes(editor, {at: cellPath});
    });
  },

  deleteTable: (editor: Editor, tableNode: NodeEntry<TableElement>): void => {
    Transforms.removeNodes(editor, {at: tableNode[1]});
  },

  setBackgroundColor: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    color: string
  ): void => {
    Transforms.setNodes(editor, {backgroundColor: color}, {at: tableNode[1]});
  },

  setCellBackgroundColor: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    selectedCells: SelectedCell[],
    color: string
  ): void => {
    if (!selectedCells.length) return;

    const tablePath = tableNode[1];

    selectedCells.forEach(({rowIndex, colIndex}) => {
      const cellPath = [...tablePath, rowIndex, colIndex];
      Transforms.setNodes(editor, {backgroundColor: color}, {at: cellPath});
    });
  },

  setColWidth: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    colIndex: number,
    width: number
  ): void => {
    const [table, tablePath] = tableNode;
    const colCount = table.children[0].children.length;
    const newColSizes = table.colSizes
      ? [...table.colSizes]
      : Array(colCount).fill(150);

    newColSizes[colIndex] = width;
    Transforms.setNodes(editor, {colSizes: newColSizes}, {at: tablePath});
  },

  setRowHeight: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    rowIndex: number,
    height: number
  ): void => {
    const tablePath = tableNode[1];
    const rowPath = [...tablePath, rowIndex];
    Transforms.setNodes(editor, {height}, {at: rowPath});
  },

  setTableSize: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    width: number,
    height: number
  ): void => {
    Transforms.setNodes(editor, {width, height}, {at: tableNode[1]});
  },

  mergeCells: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    selectedCells: SelectedCell[]
  ): void => {
    if (selectedCells.length < 2) return;

    const [, tablePath] = tableNode;
    const rowIndices = selectedCells.map((c) => c.rowIndex);
    const colIndices = selectedCells.map((c) => c.colIndex);

    const minRow = Math.min(...rowIndices);
    const maxRow = Math.max(...rowIndices);
    const minCol = Math.min(...colIndices);
    const maxCol = Math.max(...colIndices);

    const rowSpan = maxRow - minRow + 1;
    const colSpan = maxCol - minCol + 1;

    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const cellPath = [...tablePath, r, c];
        if (r === minRow && c === minCol) {
          Transforms.setNodes(
            editor,
            {rowSpan, colSpan, merged: false},
            {at: cellPath}
          );
        } else {
          Transforms.setNodes(editor, {merged: true}, {at: cellPath});
        }
      }
    }
  },

  unmergeCells: (
    editor: Editor,
    tableNode: NodeEntry<TableElement>,
    selectedCells: SelectedCell[]
  ): void => {
    if (!tableNode || !selectedCells || selectedCells.length === 0) return;
    const [table, tablePath] = tableNode;

    Editor.withoutNormalizing(editor, () => {
      selectedCells.forEach(({rowIndex, colIndex}) => {
        const cell = table.children[rowIndex].children[colIndex] as any;

        if (cell.colSpan > 1 || cell.rowSpan > 1) {
          const width = cell.colSpan ?? 1;
          const height = cell.rowSpan ?? 1;
          const masterPath = [...tablePath, rowIndex, colIndex];
          Transforms.setNodes(
            editor,
            {colSpan: 1, rowSpan: 1},
            {at: masterPath}
          );

          for (let r = rowIndex; r < rowIndex + height; r++) {
            for (let c = colIndex; c < colIndex + width; c++) {
              if (r === rowIndex && c === colIndex) continue;
              const cellPath = [...tablePath, r, c];
              Transforms.setNodes(
                editor,
                {merged: false, colSpan: 1, rowSpan: 1},
                {at: cellPath}
              );
            }
          }
        }
      });
    });
  }
};

// ==================== MAIN COMPONENT ====================

interface TableRendererProps {
  attributes: Record<string, any>;
  children: ReactNode;
  element: TableElement;
  style?: React.CSSProperties;
}

const TableRenderer: React.FC<TableRendererProps> = ({
  attributes,
  children,
  element,
  style = {}
}) => {
  const editor = useSlateStatic();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerType, setColorPickerType] = useState<
    'cell' | 'table' | null
  >(null);
  const [selectedCells, setSelectedCells] = useState<SelectedCell[]>([]);
  const [colorPickerAnchorRect, setColorPickerAnchorRect] =
    useState<DOMRect | null>(null);
  const [actionIndices, setActionIndices] = useState<SelectedCell>({
    rowIndex: 0,
    colIndex: 0
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const [renderUpwards, setRenderUpwards] = useState(false);
  const [resizing, setResizing] = useState<ResizeState | null>(null);
  const [isDraggingOffset, setIsDraggingOffset] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartMargin, setDragStartMargin] = useState(0);
  const [localMarginLeft, setLocalMarginLeft] = useState<number | null>(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Resizing Effect
  useEffect(() => {
    if (!resizing || !tableRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing) return;

      if (resizing.type === 'col' && resizing.index !== undefined) {
        const deltaX = e.clientX - resizing.startX;
        const newWidth = Math.max(50, resizing.startVal + deltaX);
        const cols = tableRef.current!.querySelectorAll('col');

        if (cols[resizing.index]) {
          (cols[resizing.index] as HTMLElement).style.width = `${newWidth}px`;
        }
      } else if (resizing.type === 'row' && resizing.index !== undefined) {
        const deltaY = e.clientY - (resizing.startY ?? 0);
        const newHeight = Math.max(30, resizing.startVal + deltaY);
        const rows = tableRef.current!.querySelectorAll('tr');

        if (rows[resizing.index]) {
          (rows[resizing.index] as HTMLElement).style.height = `${newHeight}px`;
        }
      } else if (resizing.type === 'table') {
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - (resizing.startY ?? 0);
        const newWidth = Math.max(100, resizing.startVal + deltaX);
        const newHeight = Math.max(50, (resizing.startHeight ?? 0) + deltaY);

        tableRef.current!.style.width = `${newWidth}px`;
        tableRef.current!.style.height = `${newHeight}px`;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!resizing) return;

      const path = ReactEditor.findPath(editor, element);

      if (resizing.type === 'col' && resizing.index !== undefined) {
        const deltaX = e.clientX - resizing.startX;
        const newWidth = Math.max(50, resizing.startVal + deltaX);
        TableUtils.setColWidth(
          editor,
          [element, path],
          resizing.index,
          newWidth
        );
      } else if (resizing.type === 'row' && resizing.index !== undefined) {
        const deltaY = e.clientY - (resizing.startY ?? 0);
        const newHeight = Math.max(30, resizing.startVal + deltaY);
        TableUtils.setRowHeight(
          editor,
          [element, path],
          resizing.index,
          newHeight
        );
      } else if (resizing.type === 'table') {
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - (resizing.startY ?? 0);
        const newWidth = Math.max(100, resizing.startVal + deltaX);
        const newHeight = Math.max(50, (resizing.startHeight ?? 0) + deltaY);
        TableUtils.setTableSize(editor, [element, path], newWidth, newHeight);
      }

      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, editor, element]);

  // Dragging offset effect
  useEffect(() => {
    if (!isDraggingOffset) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX;
      const newMargin = Math.max(0, dragStartMargin + deltaX);
      setLocalMarginLeft(newMargin);
    };

    const handleMouseUp = () => {
      const deltaX = dragStartX
        ? ((window.event as MouseEvent | undefined)?.clientX ?? 0) - dragStartX
        : 0;
      const newMargin = Math.max(0, dragStartMargin + deltaX);
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, {marginLeft: newMargin}, {at: path});

      setIsDraggingOffset(false);
      setLocalMarginLeft(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingOffset, dragStartX, dragStartMargin, editor, element]);

  const startDragging = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    let initialMargin =
      typeof element.marginLeft === 'number' ? element.marginLeft : 0;
    if (typeof initialMargin !== 'number' && tableRef.current) {
      const st = window.getComputedStyle(tableRef.current);
      initialMargin = parseFloat(st.marginLeft) || 0;
    }

    setIsDraggingOffset(true);
    setDragStartX(e.clientX);
    setDragStartMargin(initialMargin);
  };

  const startResizeCol = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
    width?: number
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    let startVal = width ?? 150;
    if (!width && tableRef.current) {
      const cols = tableRef.current.querySelectorAll('col');
      if (cols[index]) {
        startVal = (cols[index] as HTMLElement).getBoundingClientRect().width;
      }
    }

    setResizing({
      type: 'col',
      index,
      startX: e.clientX,
      startVal
    });
  };

  const startResizeRow = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
    height?: number
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    let startVal = height ?? 30;
    if (!height && tableRef.current) {
      const rows = tableRef.current.querySelectorAll('tr');
      if (rows[index]) {
        startVal = (rows[index] as HTMLElement).getBoundingClientRect().height;
      }
    }

    setResizing({
      type: 'row',
      index,
      startX: e.clientX,
      startY: e.clientY,
      startVal
    });
  };

  const startResizeTable = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!tableRef.current) return;

    const rect = tableRef.current.getBoundingClientRect();

    setResizing({
      type: 'table',
      startX: e.clientX,
      startY: e.clientY,
      startVal: rect.width,
      startHeight: rect.height
    });
  };

  const toggleMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    if (!isMenuOpen) {
      if (triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportBottom = window.innerHeight;
        const spaceToViewportBottom = viewportBottom - triggerRect.bottom;

        const pageElement = triggerRef.current.closest('.paginated-page');
        let spaceToPageBottom = 9999;
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          spaceToPageBottom = pageRect.bottom - triggerRect.bottom;
        }

        setRenderUpwards(
          Math.min(spaceToViewportBottom, spaceToPageBottom) < 250
        );
      }

      const path = ReactEditor.findPath(editor, element);
      let rowIndex = 0;
      let colIndex = 0;
      const newSelectedCells: SelectedCell[] = [];
      const {selection} = editor;

      if (selection) {
        const cells = Array.from(
          Editor.nodes(editor, {
            at: selection,
            match: (n): n is TableCellElement =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === 'table-cell'
          })
        );

        let firstFound = false;
        for (const [, cellPath] of cells) {
          if (Path.isDescendant(cellPath, path)) {
            const relativePath = cellPath.slice(path.length);
            const rIdx = relativePath[0];
            const cIdx = relativePath[1];

            newSelectedCells.push({rowIndex: rIdx, colIndex: cIdx});

            if (!firstFound) {
              rowIndex = rIdx;
              colIndex = cIdx;
              firstFound = true;
            }
          }
        }
      }

      setActionIndices({rowIndex, colIndex});
      setSelectedCells(
        newSelectedCells.length > 0
          ? newSelectedCells
          : [{rowIndex: 0, colIndex: 0}]
      );
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const handleAction = (action: string, e?: React.MouseEvent): void => {
    // Capture the anchor rect BEFORE closing the menu so the DOM node is still mounted
    if (e) {
      setColorPickerAnchorRect(e.currentTarget.getBoundingClientRect());
    }
    setIsMenuOpen(false);
    const path = ReactEditor.findPath(editor, element);
    const tableNode: NodeEntry<TableElement> = [element, path];
    const {rowIndex, colIndex} = actionIndices;

    switch (action) {
      case 'insertRowAbove':
        TableUtils.insertRow(editor, tableNode, rowIndex, 'above');
        break;
      case 'insertRowBelow':
        TableUtils.insertRow(editor, tableNode, rowIndex, 'below');
        break;
      case 'insertColLeft':
        TableUtils.insertColumn(editor, tableNode, colIndex, 'left');
        break;
      case 'insertColRight':
        TableUtils.insertColumn(editor, tableNode, colIndex, 'right');
        break;
      case 'deleteRow':
        TableUtils.deleteRow(editor, tableNode, rowIndex);
        break;
      case 'deleteCol':
        TableUtils.deleteColumn(editor, tableNode, colIndex);
        break;
      case 'deleteTable':
        TableUtils.deleteTable(editor, tableNode);
        break;
      case 'toggleHeaderRow':
        Transforms.setNodes(
          editor,
          {headerRow: !element.headerRow},
          {at: path}
        );
        break;
      case 'toggleStriped':
        Transforms.setNodes(editor, {striped: !element.striped}, {at: path});
        break;
      case 'setCellBackground':
        setColorPickerType('cell');
        setShowColorPicker(true);
        break;
      case 'setTableBackground':
        setColorPickerType('table');
        setShowColorPicker(true);
        break;
      case 'mergeCells':
        TableUtils.mergeCells(editor, tableNode, selectedCells);
        break;
      case 'unmergeCells':
        TableUtils.unmergeCells(editor, tableNode, selectedCells);
        break;
    }
  };

  const tableClass = `editor-table ${
    element.headerRow ? 'has-header-row' : ''
  } ${element.striped ? 'striped' : ''}`;

  const colCount = element.children[0]?.children.length ?? 0;
  const colSizes = element.colSizes ?? Array(colCount).fill('auto');

  const tableStyle: React.CSSProperties = {
    width: element.width ?? 'auto',
    height: element.height ?? 'auto',
    minWidth: '50px',
    tableLayout: element.colSizes ? 'fixed' : 'auto',
    borderCollapse: 'collapse',
    marginBottom: '1em',
    backgroundColor: element.backgroundColor ?? 'transparent',
    ...style
  };

  const currentMarginLeft =
    localMarginLeft !== null
      ? `${localMarginLeft}px`
      : typeof element.marginLeft === 'number'
        ? `${element.marginLeft}px`
        : element.align === 'center' || element.align === 'right'
          ? 'auto'
          : '0';

  const setTableRef = useCallback(
    (node: HTMLTableElement | null) => {
      // Update internal ref
      tableRef.current = node;

      // Forward ref to Slate attributes
      if (attributes.ref) {
        if (typeof attributes.ref === 'function') {
          attributes.ref(node);
        } else if (
          attributes.ref &&
          typeof attributes.ref === 'object' &&
          'current' in attributes.ref
        ) {
          (
            attributes.ref as React.MutableRefObject<HTMLTableElement | null>
          ).current = node;
        }
      }
    },
    [attributes.ref]
  );

  return (
    <div className="table-renderer-wrapper">
      <div
        style={{
          position: 'relative',
          zIndex: isMenuOpen ? 100 : 'auto',
          display: element.width === '100%' ? 'block' : 'inline-block',
          width: element.width === '100%' ? '100%' : 'fit-content',
          maxWidth: '100%',
          marginLeft: currentMarginLeft,
          marginRight: element.align === 'center' ? 'auto' : '0'
        }}
      >
        <div
          ref={triggerRef}
          className={`table-menu-trigger ${isMenuOpen ? 'active' : ''}`}
          onMouseDown={toggleMenu}
          contentEditable={false}
        >
          ⋮
        </div>

        <div
          className="table-drag-handle"
          onMouseDown={startDragging}
          contentEditable={false}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              fill="currentColor"
              // eslint-disable-next-line max-len
              d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
            />
          </svg>
        </div>

        {isMenuOpen && (
          <div
            className={`table-menu ${renderUpwards ? 'render-upwards' : ''}`}
            ref={menuRef}
            style={
              renderUpwards
                ? {top: 'auto', bottom: '100%', marginBottom: '4px'}
                : {}
            }
          >
            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('insertRowAbove');
              }}
            >
              Insert row above
            </div>
            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('insertRowBelow');
              }}
            >
              Insert row below
            </div>
            <div className="table-menu-separator" />

            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('insertColLeft');
              }}
            >
              Insert column left
            </div>
            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('insertColRight');
              }}
            >
              Insert column right
            </div>
            <div className="table-menu-separator" />

            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('deleteRow');
              }}
            >
              Delete row
            </div>
            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('deleteCol');
              }}
            >
              Delete column
            </div>
            <div
              className="table-menu-item danger"
              onClick={() => {
                handleAction('deleteTable');
              }}
            >
              Delete table
            </div>
            <div className="table-menu-separator" />

            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('toggleHeaderRow');
              }}
            >
              {element.headerRow ? '✓ Header Row' : 'Header Row'}
            </div>
            <div
              className="table-menu-item"
              onClick={() => {
                handleAction('toggleStriped');
              }}
            >
              {element.striped ? '✓ Striped Rows' : 'Striped Rows'}
            </div>
            <div
              className="table-menu-item"
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction('setCellBackground', e);
              }}
            >
              Cell Background
            </div>
            <div
              className="table-menu-item"
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction('setTableBackground', e);
              }}
            >
              Table Background
            </div>
            <div className="table-menu-separator"></div>
            {selectedCells.length > 1 && (
              <div
                className="table-menu-item"
                onClick={() => {
                  handleAction('mergeCells');
                }}
              >
                Merge Cells
              </div>
            )}
            {selectedCells.some(({rowIndex, colIndex}) => {
              const cell = element.children[rowIndex]?.children[
                colIndex
              ] as any;

              return cell && (cell.colSpan > 1 || cell.rowSpan > 1);
            }) && (
              <div
                className="table-menu-item"
                onClick={() => {
                  handleAction('unmergeCells');
                }}
              >
                Unmerge Cells
              </div>
            )}
          </div>
        )}

        <ColorPickerModal
          isOpen={showColorPicker}
          anchorRect={colorPickerAnchorRect}
          onClose={() => {
            setShowColorPicker(false);
            setColorPickerAnchorRect(null);
          }}
          title={
            colorPickerType === 'cell'
              ? 'Cell Background Color'
              : 'Table Background Color'
          }
          initialColor={
            colorPickerType === 'table'
              ? element.backgroundColor ?? '#ffffff'
              : '#ffffff'
          }
          onChange={(color: string) => {
            const path = ReactEditor.findPath(editor, element);
            if (colorPickerType === 'table') {
              TableUtils.setBackgroundColor(editor, [element, path], color);
            } else {
              TableUtils.setCellBackgroundColor(
                editor,
                [element, path],
                selectedCells,
                color
              );
            }
            setShowColorPicker(false);
          }}
        />

        <table
          className={tableClass}
          {...attributes}
          style={tableStyle}
          ref={setTableRef}
        >
          <colgroup contentEditable={false}>
            {colSizes.map((width: string | number, index: number) => (
              <col
                key={index}
                style={{width: width === 'auto' ? undefined : `${width}px`}}
              />
            ))}
          </colgroup>

          <tbody style={{border: '1px solid #ccc'}}>
            {React.Children.map(children, (rowChild, rIndex) => {
              if (!React.isValidElement(rowChild)) return rowChild;

              const row = rowChild as ReactElement;
              const rowNode = element.children[rIndex] as
                | TableRowElement
                | undefined;
              const additionalStyle: React.CSSProperties = rowNode?.height
                ? {height: `${rowNode.height}px`}
                : {};

              const cells = React.Children.map(
                row.props.children,
                (cellChild, cIndex) => {
                  if (!React.isValidElement(cellChild)) return cellChild;

                  const cell = cellChild as ReactElement;
                  const extraChildren: ReactNode[] = [];
                  const cellStyle: React.CSSProperties = {};

                  if (rIndex === 0) {
                    cellStyle.zIndex = 100 - cIndex;
                    extraChildren.push(
                      <div
                        key={`col-handle-${cIndex}`}
                        className="resize-handle-col"
                        onMouseDown={(e) => {
                          startResizeCol(e, cIndex);
                        }}
                        contentEditable={false}
                      />
                    );
                  }

                  if (cIndex === 0) {
                    // Fixed zIndex calculation
                    cellStyle.zIndex =
                      ((cellStyle.zIndex as number) ?? 0) + (100 - rIndex);
                    extraChildren.push(
                      <div
                        key={`row-handle-${rIndex}`}
                        className="resize-handle-row"
                        onMouseDown={(e) => {
                          startResizeRow(e, rIndex);
                        }}
                        contentEditable={false}
                      />
                    );
                  }

                  return React.cloneElement(cell, {
                    style: {
                      ...((cell.props.style as React.CSSProperties) || {}),
                      ...cellStyle
                    },
                    children: (
                      <>
                        {cell.props.children}
                        {extraChildren}
                      </>
                    )
                  });
                }
              );

              return React.cloneElement(row, {
                style: {
                  ...((row.props.style as React.CSSProperties) || {}),
                  ...additionalStyle
                },
                children: cells
              });
            })}
          </tbody>
        </table>

        <div
          className="resize-handle-table"
          onMouseDown={startResizeTable}
          contentEditable={false}
        />
      </div>
    </div>
  );
};

export default TableRenderer;
